import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useRef,
	ReactNode,
	useMemo
} from 'react';
import { useLocation } from 'react-router-dom';

interface CachedRoute {
	path: string;
	element: ReactNode;
	isActive: boolean;
	timestamp: number;
	accessCount: number;
}

interface RouteCacheContextValue {
	cachedRoutes: Map<string, CachedRoute>;
	cacheRoute: (path: string, element: ReactNode) => void;
	activateRoute: (path: string) => void;
	deactivateAllRoutes: () => void;
	clearCache: () => void;
	removeCachedRoute: (path: string) => void;
	getCachedRoute: (path: string) => CachedRoute | undefined;
	isCached: (path: string) => boolean;
}

const RouteCacheContext = createContext<RouteCacheContextValue | undefined>(
	undefined
);

interface RouteCacheProviderProps {
	children: ReactNode;
	maxCacheSize?: number;
}

export const RouteCacheProvider: React.FC<RouteCacheProviderProps> = ({
	children,
	maxCacheSize = 15
}) => {
	const [cachedRoutes, setCachedRoutes] = useState<Map<string, CachedRoute>>(
		new Map()
	);

	const activePathRef = useRef<string>('');

	const cacheRoute = useCallback(
		(path: string, element: ReactNode) => {
			if (activePathRef.current === path) {
				return;
			}

			setCachedRoutes(prev => {
				const newCache = new Map(prev);
				const existingRoute = newCache.get(path);

				if (existingRoute) {
					existingRoute.isActive = true;
					existingRoute.timestamp = Date.now();
					existingRoute.accessCount += 1;
					existingRoute.element = element;

					newCache.forEach((route, routePath) => {
						if (routePath !== path) {
							route.isActive = false;
						}
					});

					activePathRef.current = path;
					return newCache;
				}

				if (newCache.size >= maxCacheSize) {
					const evictionCandidates = Array.from(newCache.entries())
						.filter(([, route]) => !route.isActive)
						.map(([routePath, route]) => {
							const ageMinutes =
								(Date.now() - route.timestamp) / 1000 / 60;
							const score = route.accessCount * 10 - ageMinutes;
							return { path: routePath, score, route };
						})
						.sort((a, b) => a.score - b.score);

					if (evictionCandidates.length > 0) {
						const toEvict = evictionCandidates[0];
						newCache.delete(toEvict.path);
					}
				}

				newCache.set(path, {
					path,
					element,
					isActive: true,
					timestamp: Date.now(),
					accessCount: 1
				});

				newCache.forEach((route, routePath) => {
					if (routePath !== path) {
						route.isActive = false;
					}
				});

				activePathRef.current = path;
				return newCache;
			});
		},
		[maxCacheSize]
	);

	const activateRoute = useCallback((path: string) => {
		if (activePathRef.current === path) {
			return;
		}

		setCachedRoutes(prev => {
			const newCache = new Map(prev);
			const targetRoute = newCache.get(path);

			if (!targetRoute) {
				return prev;
			}

			newCache.forEach(route => {
				route.isActive = false;
			});
			targetRoute.isActive = true;
			targetRoute.timestamp = Date.now();
			targetRoute.accessCount += 1;

			activePathRef.current = path;
			return newCache;
		});
	}, []);

	const deactivateAllRoutes = useCallback(() => {
		// Only update if there's an active route
		if (activePathRef.current === '') {
			return;
		}

		setCachedRoutes(prev => {
			// Check if any route is actually active
			const hasActiveRoutes = Array.from(prev.values()).some(route => route.isActive);
			if (!hasActiveRoutes) {
				return prev; // No change needed
			}

			const newCache = new Map(prev);
			newCache.forEach(route => {
				route.isActive = false;
			});
			activePathRef.current = '';
			return newCache;
		});
	}, []);

	const clearCache = useCallback(() => {
		setCachedRoutes(new Map());
		activePathRef.current = '';
	}, []);

	const removeCachedRoute = useCallback((path: string) => {
		setCachedRoutes(prev => {
			const newCache = new Map(prev);
			const existed = newCache.delete(path);

			if (!existed) {
				return prev;
			}

			if (activePathRef.current === path) {
				activePathRef.current = '';
			}

			return newCache;
		});
	}, []);

	const getCachedRoute = useCallback(
		(path: string): CachedRoute | undefined => {
			return cachedRoutes.get(path);
		},
		[cachedRoutes]
	);

	const isCached = useCallback(
		(path: string): boolean => {
			return cachedRoutes.has(path);
		},
		[cachedRoutes]
	);

	const contextValue = useMemo<RouteCacheContextValue>(
		() => ({
			cachedRoutes,
			cacheRoute,
			activateRoute,
			deactivateAllRoutes,
			clearCache,
			removeCachedRoute,
			getCachedRoute,
			isCached
		}),
		[
			cachedRoutes,
			cacheRoute,
			activateRoute,
			deactivateAllRoutes,
			clearCache,
			removeCachedRoute,
			getCachedRoute,
			isCached
		]
	);

	return (
		<RouteCacheContext.Provider value={contextValue}>
			{children}
		</RouteCacheContext.Provider>
	);
};

export const useRouteCache = (): RouteCacheContextValue => {
	const context = useContext(RouteCacheContext);

	if (!context) {
		throw new Error(
			'useRouteCache must be used within a RouteCacheProvider'
		);
	}

	return context;
};

export const useCacheCurrentRoute = (element: ReactNode) => {
	const location = useLocation();
	const { cacheRoute, activateRoute } = useRouteCache();
	const prevPathRef = useRef<string>('');

	React.useEffect(() => {
		const currentPath = location.pathname;

		if (prevPathRef.current === currentPath) {
			return;
		}

		cacheRoute(currentPath, element);
		activateRoute(currentPath);
		prevPathRef.current = currentPath;
	}, [location.pathname, element, cacheRoute, activateRoute]);
};
